import _ from 'lodash'

import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { cn } from '@/utils/helpers'

// Shared cell renderers + column definitions for the Course Modules tables so
// the Admin (RescueX) and facility lists stay in parity. Name / Edit / Delete
// stay local to each table since their navigation and permission gating differ
// by scope; everything else is identical and lives here.

// [type, plural] for the per-type counts shown in the Content Types column
// (e.g. "1 Video, 4 Videos, 3 Articulate"). The type doubles as the singular;
// Articulate / PowerPoint / "Quiz Only" read the same either way.
const CONTENT_TYPE_LABELS = [
  ['Video', 'Videos'],
  ['Articulate', 'Articulate'],
  ['PDF', 'PDFs'],
  ['PowerPoint', 'PowerPoints'],
  ['Quiz Only', 'Quiz Only'],
]

export const formatContentTypeCounts = (contents) => {
  const counts = _.countBy(contents, 'type')
  const parts = CONTENT_TYPE_LABELS.filter(([type]) => counts[type]).map(
    ([type, plural]) => `${counts[type]} ${counts[type] === 1 ? type : plural}`,
  )
  // Surface any unexpected type rather than silently dropping it.
  const known = new Set(CONTENT_TYPE_LABELS.map(([type]) => type))
  Object.entries(counts).forEach(([type, count]) => {
    if (!known.has(type)) parts.push(`${count} ${type}`)
  })
  return parts.join(', ')
}

const STATUS_STYLES = {
  Draft: 'bg-primary-muted text-primary',
  Published: 'bg-primary text-white',
  Archived: 'bg-muted-foreground text-white',
}

export const formatModuleUpdatedAt = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export const statusColumn = {
  accessorKey: 'state',
  id: 'state',
  header: ({ column }) => <SortableColumnHeader column={column} label="Status" />,
  cell: ({ row }) => (
    <span
      className={cn('rounded-full px-2 py-1 text-xs uppercase', STATUS_STYLES[row.original.state])}
    >
      {row.original.state}
    </span>
  ),
}

export const contentTypesColumn = {
  id: 'contentTypes',
  header: () => <span className="uppercase">Content Types</span>,
  sortable: false,
  cell: ({ row }) =>
    row.original.contents?.length ? (
      <span>{formatContentTypeCounts(row.original.contents)}</span>
    ) : (
      <span className="text-muted-foreground">No Content</span>
    ),
}

export const hasQuizColumn = {
  id: 'hasQuiz',
  header: () => <span className="uppercase">Has Quiz</span>,
  size: 100,
  cell: ({ row }) => {
    const hasQuiz = row.original.contents?.some((c) => c.quiz !== null)
    return (
      <span
        className={cn(
          'rounded-full px-2 py-1 text-xs uppercase',
          hasQuiz ? 'bg-primary text-white' : 'bg-muted-foreground text-white',
        )}
      >
        {hasQuiz ? 'Yes' : 'No'}
      </span>
    )
  },
}

export const inUseColumn = {
  id: 'certificateCount',
  header: () => <span className="uppercase">In Use</span>,
  size: 120,
  sortable: false,
  cell: ({ row }) => `${row.original.certificateCount ?? 0} course(s)`,
}

export const lastUpdatedColumn = {
  accessorKey: 'modifiedAt',
  id: 'modifiedAt',
  header: ({ column }) => <SortableColumnHeader column={column} label="Last Updated" />,
  cell: ({ row }) => formatModuleUpdatedAt(row.original.modifiedAt),
}
